import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Agent } from '@conversia/shared-types';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { getAgentReport } from '@/api/assistants.api';

/**
 * "Qué hizo" un agente en un rango: hoy / 7 / 30 días o rango custom con
 * calendario. Los contadores de leads/escalados existen desde el despliegue de
 * esta versión (se avisa en el pie, sin inventar histórico).
 */

type Rango = 'hoy' | '7' | '30' | 'custom';

const DAY = 24 * 60 * 60 * 1000;

function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-surface-border p-3 dark:border-slate-700">
      <p className="text-xs text-slate-400">{label}</p>
      <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">{value}</p>
    </div>
  );
}

const LEAD_LABEL: Record<string, string> = {
  CALIENTE: 'calientes',
  TIBIO: 'tibios',
  FRIO: 'fríos',
  CERRADO: 'cerrados',
  NO_CERRADO: 'no cerrados',
  sin_clasificar: 'sin clasificar',
};

export function AgentReportModal({
  agent,
  onClose,
}: {
  agent: Agent | null;
  onClose: () => void;
}) {
  const [rango, setRango] = useState<Rango>('7');
  const [customDesde, setCustomDesde] = useState('');
  const [customHasta, setCustomHasta] = useState('');

  const { desde, hasta } = useMemo(() => {
    const ahora = new Date();
    if (rango === 'custom' && customDesde && customHasta) {
      return {
        desde: new Date(`${customDesde}T00:00:00`),
        hasta: new Date(`${customHasta}T23:59:59.999`),
      };
    }
    if (rango === 'hoy') {
      const inicio = new Date(ahora);
      inicio.setHours(0, 0, 0, 0);
      return { desde: inicio, hasta: ahora };
    }
    const dias = rango === '30' ? 30 : 7;
    return { desde: new Date(ahora.getTime() - dias * DAY), hasta: ahora };
  }, [rango, customDesde, customHasta]);

  const report = useQuery({
    queryKey: ['agent-report', agent?.id, desde.getTime(), hasta.getTime()],
    queryFn: () => getAgentReport(agent!.id, desde, hasta),
    enabled: Boolean(agent) && desde < hasta,
  });

  const r = report.data;
  const esCli = agent?.engineType === 'cli';

  return (
    <Modal
      open={Boolean(agent)}
      onClose={onClose}
      size="lg"
      title={`Qué hizo ${agent?.name ?? ''}`}
      description="Actividad del agente en el período elegido."
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {(
          [
            ['hoy', 'Hoy'],
            ['7', 'Últimos 7 días'],
            ['30', 'Últimos 30 días'],
            ['custom', 'Rango…'],
          ] as [Rango, string][]
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => setRango(value)}
            className={`rounded-full px-3 py-1 text-xs font-medium ${
              rango === value
                ? 'bg-brand-500 text-white'
                : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
            }`}
          >
            {label}
          </button>
        ))}
        {rango === 'custom' && (
          <span className="flex items-center gap-1.5 text-sm">
            <input
              type="date"
              value={customDesde}
              onChange={(e) => setCustomDesde(e.target.value)}
              className="rounded-md border border-surface-border bg-white px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-900"
            />
            →
            <input
              type="date"
              value={customHasta}
              onChange={(e) => setCustomHasta(e.target.value)}
              className="rounded-md border border-surface-border bg-white px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-900"
            />
          </span>
        )}
      </div>

      {report.isLoading || !r ? (
        <div className="flex justify-center py-10">
          <Spinner size={24} />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <Metric label="Turnos atendidos" value={r.turnos} />
            <Metric label="Mensajes enviados" value={r.mensajesEnviados} />
            <Metric
              label="Respuesta promedio"
              value={r.promedioRespuestaMs != null ? `${(r.promedioRespuestaMs / 1000).toFixed(1)} s` : '—'}
            />
            <Metric label="Leads registrados" value={r.leadsRegistrados} />
            <Metric label="Escalados a humano" value={r.escaladosAHumano} />
            {esCli ? (
              <Metric label="Usos CLI (suscripción)" value={r.usosCli} />
            ) : (
              r.costoUsd != null && (
                <Metric label="Costo estimado" value={`US$${r.costoUsd.toFixed(2)}`} />
              )
            )}
          </div>
          <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
            Tokens: {r.inputTokens.toLocaleString()} entrada / {r.outputTokens.toLocaleString()}{' '}
            salida.
            {r.leadsRegistrados > 0 && (
              <>
                {' '}
                Leads:{' '}
                {Object.entries(r.porLeadStatus)
                  .map(([k, v]) => `${v} ${LEAD_LABEL[k] ?? k.toLowerCase()}`)
                  .join(', ')}
                .
              </>
            )}
          </p>
          <p className="mt-2 text-[11px] text-slate-400">
            Los contadores de leads y escalados existen desde esta versión: períodos anteriores
            los muestran en cero (no se inventa histórico).
          </p>
        </>
      )}
    </Modal>
  );
}
